import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import ZimbraUserManagementTab from '../components/ZimbraUserManagementTab';
import FileSettingTab from '../components/FileSettingTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Badge } from '../components/ui/badge';
import {
    Building, FolderKanban, Users, Plus, Trash2, Edit, Loader2, Crown,
    BarChart3, CheckCircle2, ListTodo, UserCog, AlertTriangle, X, Settings, Mail, FolderOpen, Search,
    ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { getInitials } from '../lib/utils';
import axios from 'axios';
import { companyAPI } from '../lib/api';
import { APIKeyManagementContent } from './APIKeyManagementPage';
import { Key } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CompanyManagementPage() {
    const { user } = useAuth();
    const [company, setCompany] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [members, setMembers] = useState([]);
    // Server-side paginated state for the Member Company tab
    const [memberPage, setMemberPage] = useState(1);
    const memberLimit = 20;
    const [memberSearch, setMemberSearch] = useState('');
    const [pagedMembers, setPagedMembers] = useState([]);
    const [memberTotal, setMemberTotal] = useState(0);
    const [memberLoading, setMemberLoading] = useState(false);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    // Modals
    const [editWsModal, setEditWsModal] = useState({ open: false, ws: null });
    const [deleteWsModal, setDeleteWsModal] = useState({ open: false, ws: null });
    const [setAdminModal, setSetAdminModal] = useState({ open: false, ws: null });
    const [wsAdmins, setWsAdmins] = useState([]);
    const [adminsLoading, setAdminsLoading] = useState(false);
    const [addMemberModal, setAddMemberModal] = useState(false);

    const [wsForm, setWsForm] = useState({ name: '', description: '', admin_user_id: '' });
    const [adminUserId, setAdminUserId] = useState('');
    const [addMemberEmail, setAddMemberEmail] = useState('');
    const [addMemberLoading, setAddMemberLoading] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchCompany = useCallback(async () => {
        try {
            const authToken = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/api/companies/my`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.data) {
                setCompany(res.data);
                return res.data;
            }
        } catch (err) {
            console.error('Not in a company');
        }
        return null;
    }, []);

    const fetchWorkspaces = useCallback(async (companyId) => {
        try {
            const authToken = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/api/companies/${companyId}/workspaces`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setWorkspaces(res.data);
        } catch (err) {
            console.error('Failed to load workspaces');
        }
    }, []);

    const fetchMembers = useCallback(async (companyId) => {
        try {
            const authToken = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/api/companies/${companyId}/members`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setMembers(Array.isArray(res.data) ? res.data : (res.data.items || []));
        } catch (err) {
            console.error('Failed to load members');
        }
    }, []);

    const fetchPagedMembers = useCallback(async (companyId, page = 1, search = '') => {
        if (!companyId) return;
        setMemberLoading(true);
        try {
            const authToken = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/companies/${companyId}/members`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                    params: {
                        page,
                        limit: memberLimit,
                        ...(search ? { search } : {}),
                    },
                }
            );
            setPagedMembers(res.data.items || []);
            setMemberTotal(res.data.total || 0);
        } catch (err) {
            console.error('Failed to load paged members');
            setPagedMembers([]);
            setMemberTotal(0);
        } finally {
            setMemberLoading(false);
        }
    }, []);

    const fetchOverview = useCallback(async (companyId) => {
        try {
            const authToken = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/api/companies/${companyId}/overview`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setOverview(res.data);
        } catch (err) {
            console.error('Failed to load overview');
        }
    }, []);

    // Debounced server-side pagination for the Member Company tab
    useEffect(() => {
        if (!company?.id) return;
        const handle = setTimeout(() => {
            fetchPagedMembers(company.id, memberPage, memberSearch);
        }, memberSearch ? 350 : 0); // debounce search input but not pagination clicks
        return () => clearTimeout(handle);
    }, [company?.id, memberPage, memberSearch, fetchPagedMembers]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const c = await fetchCompany();
            if (c && c.my_role === 'owner') {
                await Promise.all([
                    fetchWorkspaces(c.id),
                    fetchMembers(c.id),
                    fetchOverview(c.id)
                ]);
            }
            setLoading(false);
        };
        init();
    }, []);

    const handleEditWorkspace = async () => {
        try {
            await axios.put(`${API_BASE}/api/companies/${company.id}/workspaces/${editWsModal.ws.id}`, 
                { name: wsForm.name, description: wsForm.description }, { headers });
            toast.success('Workspace diperbarui');
            setEditWsModal({ open: false, ws: null });
            fetchWorkspaces(company.id);
        } catch (err) {
            toast.error('Gagal memperbarui workspace');
        }
    };

    const handleDeleteWorkspace = async () => {
        try {
            await axios.delete(`${API_BASE}/api/companies/${company.id}/workspaces/${deleteWsModal.ws.id}`, { headers });
            toast.success('Workspace dihapus');
            setDeleteWsModal({ open: false, ws: null });
            fetchWorkspaces(company.id);
            fetchOverview(company.id);
        } catch (err) {
            toast.error('Gagal menghapus workspace');
        }
    };

    const fetchWorkspaceAdmins = useCallback(async (workspaceId) => {
        if (!company) return;
        setAdminsLoading(true);
        try {
            const res = await axios.get(
                `${API_BASE}/api/companies/${company.id}/workspaces/${workspaceId}/admins`,
                { headers }
            );
            setWsAdmins(res.data || []);
        } catch (err) {
            console.error('Failed to load admins', err);
            setWsAdmins([]);
        } finally {
            setAdminsLoading(false);
        }
    }, [company, headers]);

    const openSetAdminModal = async (ws) => {
        setSetAdminModal({ open: true, ws });
        setWsAdmins([]);
        await fetchWorkspaceAdmins(ws.id);
    };

    const handleAddAdmin = async (userId) => {
        if (!userId) return;
        try {
            await axios.put(
                `${API_BASE}/api/companies/${company.id}/workspaces/${setAdminModal.ws.id}/set-admin`,
                { admin_user_id: userId }, { headers }
            );
            toast.success('Admin ditambahkan');
            await fetchWorkspaceAdmins(setAdminModal.ws.id);
            fetchWorkspaces(company.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menambah admin');
        }
    };

    const handleRemoveAdmin = async (userId) => {
        try {
            await axios.delete(
                `${API_BASE}/api/companies/${company.id}/workspaces/${setAdminModal.ws.id}/admins/${userId}`,
                { headers }
            );
            toast.success('Admin dihapus');
            await fetchWorkspaceAdmins(setAdminModal.ws.id);
            fetchWorkspaces(company.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menghapus admin');
        }
    };

    const handleAddMember = async () => {
        if (!addMemberEmail.trim()) return;
        setAddMemberLoading(true);
        try {
            // Search user by email via company API
            const userRes = await axios.get(
                `${API_BASE}/api/companies/${company.id}/search-user?email=${encodeURIComponent(addMemberEmail.trim())}`,
                { headers }
            );
            const foundUser = userRes.data;
            await axios.post(`${API_BASE}/api/companies/${company.id}/members`,
                { user_id: foundUser.id }, { headers });
            toast.success('Member Company ditambahkan');
            setAddMemberModal(false);
            setAddMemberEmail('');
            fetchMembers(company.id);
            fetchPagedMembers(company.id, memberPage, memberSearch);
            fetchOverview(company.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menambahkan member company');
        } finally {
            setAddMemberLoading(false);
        }
    };

    const handleRemoveMember = async (userId) => {
        try {
            await axios.delete(`${API_BASE}/api/companies/${company.id}/members/${userId}`, { headers });
            toast.success('Member Company dihapus');
            fetchMembers(company.id);
            fetchPagedMembers(company.id, memberPage, memberSearch);
            fetchOverview(company.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menghapus member company');
        }
    };

    if (loading) {
        return (
            <Layout title="Company Management">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!company || company.my_role !== 'owner') {
        return (
            <Layout title="Company Management">
                <div className="p-6 text-center">
                    <Building className="w-12 h-12 mx-auto text-brand-text-muted mb-4" />
                    <h2 className="text-lg font-semibold text-brand-text-primary">Akses Ditolak</h2>
                    <p className="text-brand-text-muted mt-2">
                        {!company ? 'Anda tidak terdaftar di company manapun.' : 'Hanya owner company yang bisa mengakses halaman ini.'}
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Company Management" className="!p-0 !overflow-hidden">
            <div className="w-full h-[calc(100vh-80px)] overflow-y-auto">
                <div className="p-6 space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="font-heading text-2xl font-bold text-brand-text-primary flex items-center gap-2">
                                <Building className="w-6 h-6 text-brand-primary" />
                                {company.name}
                            </h1>
                            <p className="text-brand-text-secondary mt-1">Company Management — v2.0.5</p>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList>
                            <TabsTrigger value="overview">
                                <BarChart3 className="w-4 h-4 mr-1" /> Overview
                            </TabsTrigger>
                            <TabsTrigger value="workspaces">
                                <FolderKanban className="w-4 h-4 mr-1" /> Workspaces
                            </TabsTrigger>
                            <TabsTrigger value="members" data-testid="member-company-tab">
                                <Users className="w-4 h-4 mr-1" /> Member Company
                            </TabsTrigger>
                            <TabsTrigger value="owners" data-testid="owners-tab">
                                <Crown className="w-4 h-4 mr-1" /> Owners
                            </TabsTrigger>
                            <TabsTrigger value="zimbra">
                                <Mail className="w-4 h-4 mr-1" /> Zimbra Mail
                            </TabsTrigger>
                            <TabsTrigger value="file" data-testid="file-setting-tab-trigger">
                                <FolderOpen className="w-4 h-4 mr-1" /> File Setting
                            </TabsTrigger>
                            <TabsTrigger value="api-keys" data-testid="api-keys-tab-trigger">
                                <Key className="w-4 h-4 mr-1" /> API Keys
                            </TabsTrigger>
                        </TabsList>

                        {/* OVERVIEW TAB */}
                        <TabsContent value="overview" className="mt-4">
                            {overview && (
                                <div className="space-y-6">
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <Card className="border-brand-border shadow-card">
                                            <CardContent className="p-4 text-center">
                                                <FolderKanban className="w-6 h-6 mx-auto text-brand-primary mb-2" />
                                                <div className="text-2xl font-bold text-brand-text-primary">{overview.total_workspaces}</div>
                                                <div className="text-xs text-brand-text-muted">Workspaces</div>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-brand-border shadow-card">
                                            <CardContent className="p-4 text-center">
                                                <ListTodo className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                                                <div className="text-2xl font-bold text-brand-text-primary">{overview.total_tasks}</div>
                                                <div className="text-xs text-brand-text-muted">Total Tasks</div>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-brand-border shadow-card">
                                            <CardContent className="p-4 text-center">
                                                <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-2" />
                                                <div className="text-2xl font-bold text-green-500">{overview.total_done}</div>
                                                <div className="text-xs text-brand-text-muted">Completed</div>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-brand-border shadow-card">
                                            <CardContent className="p-4 text-center">
                                                <Users className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                                                <div className="text-2xl font-bold text-brand-text-primary">{overview.total_members}</div>
                                                <div className="text-xs text-brand-text-muted">Members</div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Overall Progress */}
                                    <Card className="border-brand-border shadow-card">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-lg">Overall Completion</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex items-center gap-4">
                                                <Progress value={overview.completion_rate} className="h-3 flex-1" />
                                                <span className="text-sm font-semibold">{overview.completion_rate}%</span>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Per-Workspace Summary */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {overview.workspaces?.map(ws => (
                                            <Card key={ws.id} className="border-brand-border shadow-card">
                                                <CardContent className="p-4">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                                            style={{ backgroundColor: ws.color || '#2F6F63' }}>
                                                            {ws.name[0]?.toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-brand-text-primary">{ws.name}</div>
                                                            <div className="text-xs text-brand-text-muted">{ws.member_count} members</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Progress value={ws.completion_rate} className="h-2 flex-1" />
                                                        <span className="text-xs font-medium">{ws.completion_rate}%</span>
                                                    </div>
                                                    <div className="flex items-center justify-between text-xs text-brand-text-muted">
                                                        <span>{ws.done_count}/{ws.task_count} tasks</span>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        {/* WORKSPACES TAB */}
                        <TabsContent value="workspaces" className="mt-4">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-medium text-brand-text-primary">Workspaces ({workspaces.length})</h2>
                            </div>

                            <div className="space-y-3">
                                {workspaces.map(ws => (
                                    <Card key={ws.id} className="border-brand-border shadow-card">
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                                        style={{ backgroundColor: ws.color || '#2F6F63' }}>
                                                        {ws.name[0]?.toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-brand-text-primary">{ws.name}</div>
                                                        <div className="flex items-center gap-3 text-xs text-brand-text-muted mt-1">
                                                            <span className="flex items-center gap-1">
                                                                <Users className="w-3 h-3" /> {ws.member_count} members
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <FolderKanban className="w-3 h-3" /> {ws.space_count} spaces
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <ListTodo className="w-3 h-3" /> {ws.task_count} tasks
                                                            </span>
                                                        </div>
                                                        {ws.admin_name && (
                                                            <div className="flex items-center gap-1 text-xs text-brand-text-muted mt-1">
                                                                <UserCog className="w-3 h-3" />
                                                                Owner: <span className="font-medium">{ws.admin_name}</span>
                                                                {ws.admin_count > 0 && (
                                                                    <span className="ml-2">
                                                                        + {ws.admin_count} admin{ws.admin_count > 1 ? 's' : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => openSetAdminModal(ws)}
                                                        className="text-brand-text-muted hover:text-brand-primary" title="Set Admin"
                                                        data-testid={`set-admin-btn-${ws.id}`}>
                                                        <Settings className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => { setWsForm({ name: ws.name, description: ws.description || '' }); setEditWsModal({ open: true, ws }); }}
                                                        className="text-brand-text-muted hover:text-brand-primary" title="Edit">
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => setDeleteWsModal({ open: true, ws })}
                                                        className="text-red-400 hover:text-red-500" title="Delete">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                                {workspaces.length === 0 && (
                                    <Card className="border-brand-border shadow-card">
                                        <CardContent className="p-12 text-center">
                                            <FolderKanban className="w-10 h-10 mx-auto text-brand-text-muted mb-3" />
                                            <p className="text-brand-text-muted">Belum ada workspace</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </TabsContent>

                        {/* MEMBERS TAB */}
                        <TabsContent value="members" className="mt-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="font-medium text-brand-text-primary">Member Company ({memberTotal})</h2>
                                    <p className="text-xs text-brand-text-muted mt-1">
                                        Daftar gabungan owner + member dari semua workspace company.
                                    </p>
                                </div>
                                <Button onClick={() => setAddMemberModal(true)}
                                    className="bg-brand-primary hover:bg-brand-primary-hover text-white"
                                    data-testid="add-member-company-btn">
                                    <Plus className="w-4 h-4 mr-1" /> Tambah Member
                                </Button>
                            </div>

                            {/* Search */}
                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
                                <Input
                                    value={memberSearch}
                                    onChange={(e) => { setMemberSearch(e.target.value); setMemberPage(1); }}
                                    placeholder="Cari nama atau email..."
                                    className="pl-9"
                                    data-testid="member-search-input" />
                            </div>

                            <div className="space-y-2" data-testid="member-list">
                                {memberLoading && (
                                    <div className="text-center py-6 text-brand-text-muted text-sm">
                                        <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Memuat...
                                    </div>
                                )}
                                {!memberLoading && pagedMembers.length === 0 && (
                                    <div className="text-center py-6 text-brand-text-muted text-sm">
                                        {memberSearch ? `Tidak ada member yang cocok dengan "${memberSearch}".` : 'Belum ada member.'}
                                    </div>
                                )}
                                {!memberLoading && pagedMembers.map(m => (
                                    <Card key={m.id} className="border-brand-border shadow-card" data-testid={`member-row-${m.user_id}`}>
                                        <CardContent className="p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-brand-primary text-white flex items-center justify-center font-medium text-sm">
                                                        {getInitials(m.user_name || m.name || '?')}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-brand-text-primary text-sm">{m.user_name || m.name || '(unknown)'}</span>
                                                            {m.role === 'owner' && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                                                        </div>
                                                        <div className="text-xs text-brand-text-muted">{m.user_email || m.email || ''}</div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                        m.role === 'owner' ? 'bg-amber-100 text-amber-700' : 'bg-brand-background text-brand-text-muted'
                                                    }`}>{m.role === 'owner' ? 'owner' : 'member'}</span>
                                                    {m.role !== 'owner' && (
                                                        <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(m.user_id)}
                                                            className="text-red-400 hover:text-red-500 h-7">
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {/* Pagination controls */}
                            {memberTotal > memberLimit && (
                                <div className="flex items-center justify-between mt-4 pt-3 border-t border-brand-border">
                                    <div className="text-xs text-brand-text-muted" data-testid="member-pagination-summary">
                                        Menampilkan {(memberPage - 1) * memberLimit + 1}–
                                        {Math.min(memberPage * memberLimit, memberTotal)} dari {memberTotal}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button variant="outline" size="sm"
                                            disabled={memberPage <= 1 || memberLoading}
                                            onClick={() => setMemberPage(p => Math.max(1, p - 1))}
                                            data-testid="member-prev-btn">
                                            <ChevronLeft className="w-4 h-4" /> Prev
                                        </Button>
                                        <span className="text-xs px-3 text-brand-text-muted">
                                            Page {memberPage} / {Math.max(1, Math.ceil(memberTotal / memberLimit))}
                                        </span>
                                        <Button variant="outline" size="sm"
                                            disabled={memberPage * memberLimit >= memberTotal || memberLoading}
                                            onClick={() => setMemberPage(p => p + 1)}
                                            data-testid="member-next-btn">
                                            Next <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        {/* OWNERS TAB */}
                        <TabsContent value="owners" className="mt-4">
                            {company && (
                                <OwnersTab
                                    companyId={company.id}
                                    members={members}
                                    currentUserId={user?.id}
                                />
                            )}
                        </TabsContent>

                        {/* ZIMBRA MAIL TAB */}
                        <TabsContent value="zimbra" className="mt-4">
                            {company && (
                                <ZimbraUserManagementTab companyId={company.id} />
                            )}
                        </TabsContent>

                        {/* FILE SETTING TAB */}
                        <TabsContent value="file" className="mt-4">
                            {company && (
                                <FileSettingTab companyId={company.id} />
                            )}
                        </TabsContent>

                        {/* API KEYS TAB */}
                        <TabsContent value="api-keys" className="mt-4">
                            <APIKeyManagementContent embedded />
                        </TabsContent>

                    </Tabs>
                </div>
            </div>

            {/* Edit Workspace Modal */}
            <Dialog open={editWsModal.open} onOpenChange={(open) => !open && setEditWsModal({ open: false, ws: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Workspace</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Nama</Label>
                            <Input value={wsForm.name} onChange={(e) => setWsForm(p => ({...p, name: e.target.value}))} className="mt-1" />
                        </div>
                        <div>
                            <Label>Deskripsi</Label>
                            <Input value={wsForm.description} onChange={(e) => setWsForm(p => ({...p, description: e.target.value}))} className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditWsModal({ open: false, ws: null })}>Batal</Button>
                        <Button onClick={handleEditWorkspace} className="bg-brand-primary hover:bg-brand-primary-hover text-white">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Workspace Modal */}
            <Dialog open={deleteWsModal.open} onOpenChange={(open) => !open && setDeleteWsModal({ open: false, ws: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" /> Hapus Workspace
                        </DialogTitle>
                        <DialogDescription>
                            Semua data (spaces, tasks, dll) di workspace <strong>{deleteWsModal.ws?.name}</strong> akan dihapus permanen.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteWsModal({ open: false, ws: null })}>Batal</Button>
                        <Button variant="destructive" onClick={handleDeleteWorkspace}>Hapus</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Set Admin Modal */}
            <Dialog open={setAdminModal.open} onOpenChange={(open) => !open && setSetAdminModal({ open: false, ws: null })}>
                <DialogContent data-testid="set-admin-modal">
                    <DialogHeader>
                        <DialogTitle>Kelola Admin Workspace</DialogTitle>
                        <DialogDescription>
                            Tambah atau hapus admin untuk workspace <strong>{setAdminModal.ws?.name}</strong>. Bisa lebih dari 1 admin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Current admins as chips */}
                        <div>
                            <Label className="text-xs">Admin Saat Ini ({wsAdmins.length})</Label>
                            <div className="flex flex-wrap gap-2 mt-2 min-h-[40px]" data-testid="ws-admin-chips">
                                {adminsLoading && (
                                    <span className="text-xs text-brand-text-muted flex items-center gap-1">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Memuat...
                                    </span>
                                )}
                                {!adminsLoading && wsAdmins.length === 0 && (
                                    <span className="text-xs text-brand-text-muted">Belum ada admin/owner.</span>
                                )}
                                {wsAdmins.map(a => (
                                    <div key={a.user_id}
                                        className={`flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border ${
                                            a.role === 'owner'
                                                ? 'bg-amber-50 border-amber-200'
                                                : 'bg-purple-50 border-purple-200'
                                        }`}
                                        data-testid={`ws-admin-chip-${a.user_id}`}>
                                        <Crown className={`w-3.5 h-3.5 ${a.role === 'owner' ? 'text-amber-600' : 'text-purple-600'}`} />
                                        <div className="flex flex-col">
                                            <span className={`text-xs font-medium ${a.role === 'owner' ? 'text-amber-900' : 'text-purple-900'}`}>{a.name || '(unknown)'}</span>
                                            <span className={`text-[10px] ${a.role === 'owner' ? 'text-amber-700' : 'text-purple-700'}`}>{a.email || ''} · {a.role}</span>
                                        </div>
                                        {a.role !== 'owner' && (
                                            <button
                                                onClick={() => handleRemoveAdmin(a.user_id)}
                                                className="ml-1 p-1 rounded-full hover:bg-purple-100"
                                                aria-label="remove admin"
                                                data-testid={`ws-admin-remove-${a.user_id}`}>
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Autocomplete to add admin */}
                        <div>
                            <Label className="text-xs">Tambah Admin</Label>
                            <AddWorkspaceAdminCombobox
                                members={members}
                                excludeIds={wsAdmins.map(a => a.user_id)}
                                onPick={handleAddAdmin}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setSetAdminModal({ open: false, ws: null })}>Tutup</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Member Modal */}
            <Dialog open={addMemberModal} onOpenChange={setAddMemberModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tambah Member Company</DialogTitle>
                        <DialogDescription>Masukkan email user yang sudah terdaftar untuk dijadikan Member Company</DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label>Email User</Label>
                        <Input value={addMemberEmail} onChange={(e) => setAddMemberEmail(e.target.value)}
                            className="mt-1" placeholder="user@email.com" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAddMemberModal(false)}>Batal</Button>
                        <Button onClick={handleAddMember} disabled={addMemberLoading}
                            className="bg-brand-primary hover:bg-brand-primary-hover text-white">
                            {addMemberLoading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                            Tambah
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

// =============================================================== sub-components
function AddWorkspaceAdminCombobox({ members, excludeIds, onPick }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const candidates = useMemo(() =>
        (members || []).filter(m =>
            !excludeIds.includes(m.user_id) &&
            (
                ((m.user_name || m.name || '').toLowerCase().includes(query.toLowerCase())) ||
                ((m.user_email || m.email || '').toLowerCase().includes(query.toLowerCase()))
            )
        ), [members, excludeIds, query]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-start mt-1" data-testid="ws-admin-combobox">
                    <Search className="w-4 h-4 mr-2 text-gray-500" /> Cari member untuk dijadikan admin...
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                    <CommandInput
                        placeholder="Ketik nama atau email..."
                        value={query}
                        onValueChange={setQuery}
                        data-testid="ws-admin-combobox-input" />
                    <CommandList>
                        <CommandEmpty>Tidak ada member yang cocok.</CommandEmpty>
                        <CommandGroup>
                            {candidates.map(m => (
                                <CommandItem
                                    key={m.user_id}
                                    value={`${m.user_name || m.name || ''} ${m.user_email || m.email || ''}`}
                                    onSelect={() => { onPick(m.user_id); setOpen(false); setQuery(''); }}
                                    data-testid={`ws-admin-option-${m.user_id}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-semibold">
                                            {(m.user_name || m.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm">{m.user_name || m.name}</span>
                                            <span className="text-xs text-gray-500">{m.user_email || m.email}</span>
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

// =============================================================== Owners Tab
function OwnersTab({ companyId, members, currentUserId }) {
    const [owners, setOwners] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [confirmTransfer, setConfirmTransfer] = React.useState(null); // user object

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const API_BASE_LOCAL = process.env.REACT_APP_BACKEND_URL;

    const fetchOwners = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_LOCAL}/api/companies/${companyId}/owners`, { headers });
            setOwners(res.data || []);
        } catch (err) {
            toast.error('Gagal memuat owners');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    React.useEffect(() => { fetchOwners(); }, [fetchOwners]);

    const handleAddOwner = async (userId) => {
        try {
            await axios.post(`${API_BASE_LOCAL}/api/companies/${companyId}/owners`,
                { user_id: userId }, { headers });
            toast.success('Owner ditambahkan');
            fetchOwners();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menambah owner');
        }
    };

    const handleRemoveOwner = async (userId, name) => {
        if (!window.confirm(`Demote ${name} dari owner kembali jadi member?`)) return;
        try {
            await axios.delete(`${API_BASE_LOCAL}/api/companies/${companyId}/owners/${userId}`,
                { headers });
            toast.success('Owner di-demote');
            fetchOwners();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal demote owner');
        }
    };

    const handleTransfer = async () => {
        if (!confirmTransfer) return;
        try {
            await axios.post(`${API_BASE_LOCAL}/api/companies/${companyId}/transfer-ownership`,
                { user_id: confirmTransfer.user_id }, { headers });
            toast.success('Primary ownership ditransfer');
            setConfirmTransfer(null);
            fetchOwners();
            // Reload setelah transfer agar role di sidebar refresh
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal transfer');
        }
    };

    const ownerIds = new Set(owners.map(o => o.user_id));
    const candidates = (members || []).filter(m => !ownerIds.has(m.user_id));
    const isPrimary = owners.find(o => o.is_primary)?.user_id === currentUserId;

    if (loading) {
        return (
            <Card className="border-brand-border shadow-card">
                <CardContent className="p-6 text-center text-brand-text-muted">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Memuat...
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-5">
            {/* Info banner */}
            <Card className="border-brand-border bg-amber-50 shadow-card">
                <CardContent className="p-4 text-sm text-amber-900 flex items-start gap-3">
                    <Crown className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                        <strong>Multi-Owner Company.</strong> Semua user dengan badge owner punya akses penuh ke
                        company. <strong>Primary owner</strong> ({isPrimary ? 'Anda' : 'user lain'})
                        {' '}adalah satu-satunya yang boleh melakukan <em>Transfer Ownership</em>.
                    </div>
                </CardContent>
            </Card>

            {/* Current Owners */}
            <Card className="border-brand-border shadow-card">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-500" />
                        Owners ({owners.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2" data-testid="owners-list">
                    {owners.map(o => (
                        <div key={o.user_id}
                            className={`flex items-center justify-between p-3 rounded border ${
                                o.is_primary
                                    ? 'bg-amber-50 border-amber-200'
                                    : 'bg-purple-50 border-purple-200'
                            }`}
                            data-testid={`owner-row-${o.user_id}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-brand-primary text-white flex items-center justify-center font-medium text-sm">
                                    {getInitials(o.name || '?')}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-brand-text-primary text-sm">
                                            {o.name || '(unknown)'}
                                            {o.user_id === currentUserId && (
                                                <span className="text-xs text-brand-text-muted ml-1">(Anda)</span>
                                            )}
                                        </span>
                                        {o.is_primary && (
                                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-500 text-white rounded font-semibold">
                                                PRIMARY
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-brand-text-muted">{o.email || ''}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {/* Transfer button — only primary owner can transfer to a NON-primary owner */}
                                {isPrimary && !o.is_primary && (
                                    <Button variant="outline" size="sm"
                                        onClick={() => setConfirmTransfer(o)}
                                        className="h-7 text-xs"
                                        data-testid={`transfer-to-${o.user_id}`}>
                                        Transfer Primary
                                    </Button>
                                )}
                                {/* Demote — primary cannot be demoted */}
                                {!o.is_primary && (
                                    <Button variant="ghost" size="sm"
                                        onClick={() => handleRemoveOwner(o.user_id, o.name)}
                                        className="text-red-400 hover:text-red-500 h-7"
                                        data-testid={`remove-owner-${o.user_id}`}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Add Owner */}
            <Card className="border-brand-border shadow-card">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Tambah Owner Baru
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-xs text-brand-text-muted mb-2">
                        Pilih dari Member Company / member workspace untuk dipromote menjadi owner. User akan punya akses penuh ke company management.
                    </p>
                    <AddWorkspaceAdminCombobox
                        members={candidates}
                        excludeIds={[]}
                        onPick={handleAddOwner}
                    />
                </CardContent>
            </Card>

            {/* Transfer confirm dialog */}
            <Dialog open={!!confirmTransfer} onOpenChange={(o) => !o && setConfirmTransfer(null)}>
                <DialogContent data-testid="transfer-confirm-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Crown className="w-5 h-5 text-amber-500" />
                            Transfer Primary Ownership
                        </DialogTitle>
                        <DialogDescription>
                            Anda akan memindahkan status <strong>Primary Owner</strong> ke
                            <strong> {confirmTransfer?.name}</strong> ({confirmTransfer?.email}).
                            <br /><br />
                            Anda akan tetap menjadi <em>owner</em> (akses penuh) tapi tidak lagi sebagai primary.
                            Tindakan ini bisa di-rollback dengan transfer kembali.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmTransfer(null)}>Batal</Button>
                        <Button onClick={handleTransfer}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="transfer-confirm-btn">
                            Ya, Transfer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
